import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  InputAdornment,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
} from '@mui/material';
import {
  School,
  Search,
  FilterList,
  Visibility,
  Close,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import CertificateDialog from '../components/CertificateDialog';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  modules: number;
  rating: number;
  enrolledUsers: number;
  isCompleted?: boolean;
  certificateEligible?: boolean;
  progress?: number;
  isEnrolled?: boolean;
}

const Courses: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  
  // Estado para el drawer de filtros en móviles
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Estados para certificado
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);

  // Verificar si la empresa permite ver certificados
  const allowViewCertificates = user?.company?.settings?.allowViewCertificates ?? true;

  // Categorías disponibles
  const categories = [
    'phishing_prevention',
    'ransomware_protection',
    'social_engineering',
    'device_security',
    'password_management',
    'data_protection',
    'email_security',
    'web_browsing',
    'mobile_security',
    'incident_response'
  ];

  // Dificultades disponibles
  const difficulties = [
    { value: 'beginner', label: 'Principiante' },
    { value: 'intermediate', label: 'Intermedio' },
    { value: 'advanced', label: 'Avanzado' }
  ];

  useEffect(() => {
    fetchCourses();
  }, []);

  // Aplicar filtros cuando cambien los valores
  useEffect(() => {
    applyFilters();
  }, [courses, searchTerm, selectedCategory, selectedDifficulty]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      
      // Obtener cursos y progreso del usuario en paralelo
      const [coursesResponse, userProgressResponse] = await Promise.all([
        api.getAllCourses(),
        api.getUserProgress().catch(() => []) // Si falla, array vacío
      ]);
      
      // Crear un mapa de progreso por courseId
      const progressMap = new Map();
      if (userProgressResponse && Array.isArray(userProgressResponse)) {
        userProgressResponse.forEach((progress: any) => {
          const courseId = typeof progress.course === 'string' 
            ? progress.course 
            : progress.course?._id || progress.course?.id;
          if (courseId) {
            progressMap.set(courseId, {
              completionPercentage: progress.completionPercentage || 0,
              status: progress.status,
              // Si el backend aún no envía el flag, status completed implica elegible
              certificateEligible:
                progress.certificateEligible ?? progress.status === 'completed',
              isEnrolled: true
            });
          }
        });
      }
      
      // Transformar los datos para que coincidan con la interfaz
      const transformedCourses = coursesResponse.courses.map((course: any) => {
        const courseId = course._id || course.id;
        const userProgress = progressMap.get(courseId);
        
        const transformedCourse = {
          id: courseId,
          title: course.title,
          description: course.description,
          category: course.category,
          difficulty: course.difficulty,
          duration: course.estimatedDuration,
          modules: course.lessons?.length || 0,
          rating: course.analytics?.averageRating || 0,
          enrolledUsers: course.analytics?.enrollments || 0,
          progress: userProgress?.completionPercentage || 0,
          isCompleted: userProgress?.status === 'completed',
          certificateEligible: userProgress?.certificateEligible ?? false,
          isEnrolled: !!userProgress
        };
        
        // Log para debugging
        if (userProgress) {
          console.log(`📊 Curso "${course.title}": ${userProgress.completionPercentage}% completado`);
        }
        
        return transformedCourse;
      });
      
      setCourses(transformedCourses);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...courses];

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por categoría
    if (selectedCategory) {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Filtro por dificultad
    if (selectedDifficulty) {
      filtered = filtered.filter(course => course.difficulty === selectedDifficulty);
    }

    setFilteredCourses(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDifficulty('');
  };

  const handleFiltersClose = () => {
    setFiltersOpen(false);
  };

  // Componente de filtros reutilizable
  const FiltersPanel = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Búsqueda */}
      <TextField
        fullWidth
        label="Buscar cursos"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Buscar por título o descripción..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {/* Chips de Categoría */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Categoría
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="Todos"
            clickable
            color={selectedCategory === '' ? 'primary' : 'default'}
            onClick={() => setSelectedCategory('')}
            sx={{ fontWeight: selectedCategory === '' ? 'bold' : 'normal' }}
          />
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={
                <span>
                  {getCategoryLabel(cat)}{' '}
                  <span style={{ fontWeight: 'bold', fontSize: 13, opacity: 0.7 }}>
                    {categoryCounts[cat] > 0 ? `(${categoryCounts[cat]})` : ''}
                  </span>
                </span>
              }
              clickable
              color={selectedCategory === cat ? 'primary' : 'default'}
              onClick={() => setSelectedCategory(cat)}
              sx={{ fontWeight: selectedCategory === cat ? 'bold' : 'normal' }}
              disabled={categoryCounts[cat] === 0}
            />
          ))}
        </Box>
      </Box>

      {/* Chips de Dificultad */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Dificultad
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="Todas"
            clickable
            color={selectedDifficulty === '' ? 'primary' : 'default'}
            onClick={() => setSelectedDifficulty('')}
            sx={{ fontWeight: selectedDifficulty === '' ? 'bold' : 'normal' }}
          />
          {difficulties.map((diff) => (
            <Chip
              key={diff.value}
              label={
                <span>
                  {diff.label}{' '}
                  <span style={{ fontWeight: 'bold', fontSize: 13, opacity: 0.7 }}>
                    {difficultyCounts[diff.value] > 0 ? `(${difficultyCounts[diff.value]})` : ''}
                  </span>
                </span>
              }
              clickable
              color={selectedDifficulty === diff.value ? 'primary' : 'default'}
              onClick={() => setSelectedDifficulty(diff.value)}
              sx={{ fontWeight: selectedDifficulty === diff.value ? 'bold' : 'normal' }}
              disabled={difficultyCounts[diff.value] === 0}
            />
          ))}
        </Box>
      </Box>

      {/* Botón limpiar filtros */}
      <Button
        variant={(searchTerm || selectedCategory || selectedDifficulty) ? "contained" : "outlined"}
        onClick={clearFilters}
        size="small"
        color={(searchTerm || selectedCategory || selectedDifficulty) ? "error" : "primary"}
        sx={{ 
          mt: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: (searchTerm || selectedCategory || selectedDifficulty) ? 'scale(1.05)' : 'none',
          }
        }}
      >
        Limpiar Filtros
      </Button>

      {/* Información de resultados */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {filteredCourses.length} de {courses.length} cursos
        </Typography>
        {(searchTerm || selectedCategory || selectedDifficulty) && (
          <Chip
            label={`Filtros activos: ${[
              searchTerm && 'Búsqueda',
              selectedCategory && 'Categoría',
              selectedDifficulty && 'Dificultad'
            ].filter(Boolean).join(', ')}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </Box>
    </Box>
  );

  const getCategoryLabel = (category: string) => {
    const categoryLabels: { [key: string]: string } = {
      'phishing_prevention': 'Prevención de Phishing',
      'ransomware_protection': 'Protección contra Ransomware',
      'social_engineering': 'Ingeniería Social',
      'device_security': 'Seguridad de Dispositivos',
      'password_management': 'Gestión de Contraseñas',
      'data_protection': 'Protección de Datos',
      'email_security': 'Seguridad de Email',
      'web_browsing': 'Navegación Web Segura',
      'mobile_security': 'Seguridad Móvil',
      'incident_response': 'Respuesta a Incidentes'
    };
    return categoryLabels[category] || category;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'Principiante';
      case 'intermediate':
        return 'Intermedio';
      case 'advanced':
        return 'Avanzado';
      default:
        return difficulty;
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };


  const handleViewCourse = (course: Course) => {
    // Siempre navegar al detalle del curso
    navigate(`/courses/${course.id}`);
  };

  const handleGenerateCertificate = async (course: Course) => {
    try {
      const certificateData = await api.generateCertificate(course.id);
      setCertificateData(certificateData);
      setCertificateDialogOpen(true);
    } catch (error: any) {
      console.error('Error generando certificado:', error);
      setError(error.response?.data?.message || 'Error generando certificado');
    }
  };

  // Calcular contadores para chips
  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat] = courses.filter(c => c.category === cat).length;
    return acc;
  }, {} as Record<string, number>);
  const difficultyCounts = difficulties.reduce((acc, diff) => {
    acc[diff.value] = courses.filter(c => c.difficulty === diff.value).length;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          <School sx={{ mr: 2, verticalAlign: 'middle' }} />
          Cursos de Ciberseguridad
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {[1, 2, 3].map((item) => (
            <Box key={item} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)' } }}>
              <Card>
                <CardContent>
                  <Skeleton variant="rectangular" height={140} />
                  <Skeleton variant="text" sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <School sx={{ mr: 2, verticalAlign: 'middle' }} />
          Cursos de Ciberseguridad
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Mejora tus habilidades en ciberseguridad con nuestros cursos especializados
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Botón de filtros para móviles */}
      {isMobile && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {filteredCourses.length} de {courses.length} cursos
          </Typography>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFiltersOpen(true)}
            sx={{ minWidth: 'auto' }}
          >
            Filtros
          </Button>
        </Box>
      )}

      {/* Layout principal responsive */}
      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        alignItems: 'flex-start',
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        {/* Filtros - Panel lateral para desktop */}
        {!isMobile && (
          <Paper sx={{ p: 3, minWidth: 280, maxWidth: 320, position: 'sticky', top: 20 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FilterList sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" color="primary">
                Filtros
              </Typography>
            </Box>
            <FiltersPanel />
          </Paper>
        )}

        {/* Lista de cursos - Contenido principal */}
        <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(300px, 1fr))',
              md: 'repeat(auto-fill, minmax(320px, 1fr))',
              lg: 'repeat(auto-fill, minmax(350px, 1fr))'
            },
            gap: 3,
            width: '100%'
          }}>
            {filteredCourses.map((course) => (
              <Card 
                key={course.id}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  }
                }}
              >
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    height: 140,
                    background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <School sx={{ fontSize: 60 }} />
                </Box>
                {course.isCompleted && (
                  <Chip
                    label="Completado"
                    color="success"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  />
                )}
                {course.isEnrolled && !course.isCompleted && (
                  <Chip
                    label="Matriculado"
                    color="info"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  />
                )}
              </Box>

              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {course.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {course.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    label={getDifficultyText(course.difficulty)}
                    color={getDifficultyColor(course.difficulty) as any}
                    size="small"
                  />
                  <Chip
                    label={getCategoryLabel(course.category)}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    ⏱️ {formatDuration(course.duration)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    📚 {course.modules} módulos
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    👥 {course.enrolledUsers} estudiantes
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    ⭐ {course.rating.toFixed(1)} ({course.enrolledUsers} reseñas)
                  </Typography>
                </Box>

                {/* Progress bar */}
                {course.progress !== undefined && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Progreso
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {course.progress}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 6,
                        backgroundColor: 'grey.200',
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${course.progress}%`,
                          height: '100%',
                          backgroundColor: 'primary.main',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Visibility />}
                  onClick={() => handleViewCourse(course)}
                  disabled={course.isEnrolled}
                  sx={{
                    background: course.isEnrolled 
                      ? 'linear-gradient(45deg, #9e9e9e 30%, #757575 90%)'
                      : 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': {
                      background: course.isEnrolled
                        ? 'linear-gradient(45deg, #9e9e9e 30%, #757575 90%)'
                        : 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #9e9e9e 30%, #757575 90%)',
                      color: 'rgba(255, 255, 255, 0.6)',
                    },
                  }}
                >
                  Detalles
                </Button>
                
                {course.certificateEligible && allowViewCertificates && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleGenerateCertificate(course)}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      borderColor: '#9c27b0',
                      color: '#9c27b0',
                      '&:hover': {
                        borderColor: '#7b1fa2',
                        backgroundColor: '#f3e5f5',
                      },
                    }}
                  >
                    🏆
                  </Button>
                )}
              </CardActions>
              </Card>
            ))}
          </Box>

      {/* Mensaje cuando no hay cursos */}
          {filteredCourses.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <School sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
                {courses.length === 0 ? 'No se encontraron cursos' : 'No hay cursos que coincidan con los filtros'}
          </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {courses.length === 0 
                  ? 'Pronto tendremos nuevos cursos disponibles'
                  : 'Intenta ajustar los filtros de búsqueda'
                }
          </Typography>
              {courses.length > 0 && (
                <Button variant="outlined" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Drawer de filtros para móviles */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={handleFiltersClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            maxWidth: '90vw',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterList sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" color="primary">
                Filtros
              </Typography>
            </Box>
            <IconButton onClick={handleFiltersClose}>
              <Close />
            </IconButton>
          </Box>
          
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <FiltersPanel />
          </Box>
        </Box>
      </Drawer>

      {/* Diálogo de certificado */}
      {certificateData && (
        <CertificateDialog
          open={certificateDialogOpen}
          onClose={() => {
            setCertificateDialogOpen(false);
            setCertificateData(null);
          }}
          studentName={certificateData.studentName}
          courseName={certificateData.courseName}
          completionDate={certificateData.completionDate}
          position={certificateData.position}
          companyName={certificateData.company?.name}
          companyLogo={certificateData.company?.logo}
          certificateSigner={certificateData.company?.certificateSigner}
          showCTOSignature={certificateData.company?.showCTOSignature ?? true}
          allowViewCertificates={allowViewCertificates}
        />
      )}

    </Box>
  );
};

export default Courses; 